import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  addDynamicField,
  createDomain,
  deleteDocument,
  deleteDomain,
  getDomain,
  getDomainById,
  updateDomain,
  uploadDocuments,
} from "../../../services/postService.services";
import { v4 as uuidv4 } from "uuid";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function ManagePreloadedDetails() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  // Tracking category editing
  const [editingCategory, setEditingCategory] = useState(null);
  const [editedCategoryName, setEditedCategoryName] = useState("");

  // State for career data
  const [cards, setCards] = useState({
    dynamic_fields: {},
    university_name_list: [],
    link_list: {},
  });
  const [categoryOrder, setCategoryOrder] = useState([]);
  const [selectedFieldId, setSelectedFieldId] = useState("");
  const [careerFields, setCareerFields] = useState([]);

  // State for adding/editing career fields
  const [newField, setNewField] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [editedField, setEditedField] = useState("");
  const [showOptions, setShowOptions] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("universities");

  // Normalize dynamic fields to ensure consistent format and track order
  const normalizeDynamicFields = (rawFields) => {
    const normalized = {};
    const newOrder = [];

    for (const [category, items] of Object.entries(rawFields || {})) {
      normalized[category] = (items || []).map((item) => {
        if (typeof item === "string") {
          return { id: uuidv4(), value: item };
        } else if (typeof item === "object" && item !== null) {
          return {
            id: item.id || uuidv4(),
            value: item.value || "",
          };
        } else {
          return { id: uuidv4(), value: "" };
        }
      });

      // Track the category order
      newOrder.push(category);
    }

    // Update the category order state
    setCategoryOrder(newOrder);

    return normalized;
  };

  // Toggle Edit Mode
  const toggleEditMode = () => {
    if (isEditing) {
      // If we're currently editing, prompt for confirmation before exiting edit mode
      if (
        window.confirm(
          "Are you sure you want to exit edit mode? Any unsaved changes will be lost."
        )
      ) {
        setIsEditing(false);
        setEditingCategory(null);
        setEditedCategoryName("");
      }
    } else {
      // If we're not editing, just enter edit mode
      setIsEditing(true);
    }
  };

  // Start editing a category
  const startEditingCategory = (category) => {
    setEditingCategory(category);
    setEditedCategoryName(category);
  };

  // Save edited category
  const saveEditedCategory = () => {
    if (!editingCategory || editingCategory === editedCategoryName) {
      // No change or no category being edited
      setEditingCategory(null);
      return;
    }

    if (!editedCategoryName.trim()) {
      toast.error("Category name cannot be empty");
      return;
    }

    const updatedDynamicFields = { ...cards.dynamic_fields };

    // Copy the items from the old category to the new one
    updatedDynamicFields[editedCategoryName] =
      updatedDynamicFields[editingCategory];

    // Delete the old category
    delete updatedDynamicFields[editingCategory];

    // Update category order to maintain the same position
    const updatedOrder = categoryOrder.map((category) =>
      category === editingCategory ? editedCategoryName : category
    );
    setCategoryOrder(updatedOrder);

    // Update the state
    setCards({
      ...cards,
      dynamic_fields: updatedDynamicFields,
    });

    // Exit editing mode
    setEditingCategory(null);
    toast.success("Category renamed successfully");
  };

  // Cancel editing category
  const cancelEditingCategory = () => {
    setEditingCategory(null);
    setEditedCategoryName("");
  };

  // Add a new card (category)
  const addCard = async () => {
    if (!selectedFieldId) {
      toast.error("Please select a career field first");
      return;
    }

    // Generate a default category name that doesn't exist yet
    let newCategory = "New Category";
    let counter = 1;
    while (cards.dynamic_fields[newCategory]) {
      newCategory = `New Category ${counter}`;
      counter++;
    }

    const dynamicFields = {
      ...cards.dynamic_fields,
      [newCategory]: [], // Add new empty category
    };

    // Update the category order
    setCategoryOrder([...categoryOrder, newCategory]);

    try {
      const data = await addDynamicField(selectedFieldId, dynamicFields);

      toast.success("New category added");

      // If we get back data with a different structure, normalize it again
      if (data && data.dynamic_fields) {
        setCards({
          ...cards,
          dynamic_fields: normalizeDynamicFields(data.dynamic_fields),
        });
      } else {
        setCards({
          ...cards,
          dynamic_fields: dynamicFields,
        });
      }
    } catch (error) {
      console.error("Error adding dynamic field:", error);
      toast.error("Failed to add category");
      // Even if there's an error, update the UI
      setCards({
        ...cards,
        dynamic_fields: dynamicFields,
      });
    }
  };

  const parseTextWithLinks = (text) => {
    if (!text || typeof text !== "string") return text;

    // Match full URLs (https://, http://, www.) and capture them fully
    const urlRegex = /((https?:\/\/[^\s]+)|(www\.[^\s]+))/gi;

    const matches = [...text.matchAll(urlRegex)];
    if (matches.length === 0) return <span>{text}</span>;

    const elements = [];
    let lastIndex = 0;

    matches.forEach((match, i) => {
      const url = match[0];
      const startIndex = match.index;

      // Add text before the link
      if (startIndex > lastIndex) {
        elements.push(
          <span key={`text-${i}`}>{text.slice(lastIndex, startIndex)}</span>
        );
      }

      // Normalize www. to full https://
      const href = url.startsWith("www.") ? `https://${url}` : url;

      elements.push(
        <a
          key={`link-${i}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 underline hover:text-blue-800 break-all"
        >
          {url}
        </a>
      );

      lastIndex = startIndex + url.length;
    });

    // Add remaining text after the last link
    if (lastIndex < text.length) {
      elements.push(<span key="text-end">{text.slice(lastIndex)}</span>);
    }

    return elements;
  };

  // Handle item update
  const handleItemUpdate = (category, itemIndex, newValue) => {
    const updatedDynamicFields = { ...cards.dynamic_fields };
    const items = [...updatedDynamicFields[category]];

    // Get the current item
    const currentItem = items[itemIndex];

    // Update the item while preserving its structure
    if (typeof currentItem === "object" && currentItem !== null) {
      // If it's already an object, just update the value
      items[itemIndex] = {
        ...currentItem,
        value: newValue,
      };
    } else {
      // If it's a string or any other type, convert to our standard object format
      items[itemIndex] = {
        id: uuidv4(),
        value: newValue,
      };
    }

    // Update the category with the modified items array
    updatedDynamicFields[category] = items;

    // Update the state
    setCards({
      ...cards,
      dynamic_fields: updatedDynamicFields,
    });
  };

  // Add new item to a category
  const handleAddItem = (category) => {
    const updatedDynamicFields = { ...cards.dynamic_fields };

    // Make sure the category exists
    if (!updatedDynamicFields[category]) {
      updatedDynamicFields[category] = [];
    }

    // Add a new item with our standard format
    updatedDynamicFields[category] = [
      ...updatedDynamicFields[category],
      { id: uuidv4(), value: "" },
    ];

    // Update the state
    setCards({
      ...cards,
      dynamic_fields: updatedDynamicFields,
    });
  };

  // Delete item from a category
  const handleDeleteItem = (category, itemIndex) => {
    if (window.confirm("Are you sure you want to delete this item?")) {
      const updatedDynamicFields = { ...cards.dynamic_fields };

      // Filter out the item to be deleted
      updatedDynamicFields[category] = updatedDynamicFields[category].filter(
        (_, i) => i !== itemIndex
      );

      // Update the state
      setCards({
        ...cards,
        dynamic_fields: updatedDynamicFields,
      });

      toast.success("Item deleted");
    }
  };

  // Delete a category
  const handleDeleteCategory = (category) => {
    if (
      window.confirm(
        `Are you sure you want to delete the "${category}" category?`
      )
    ) {
      const updatedDynamicFields = { ...cards.dynamic_fields };

      // Delete the category
      delete updatedDynamicFields[category];

      // Update category order to remove the deleted category
      const updatedOrder = categoryOrder.filter((cat) => cat !== category);
      setCategoryOrder(updatedOrder);

      // Update the state
      setCards({
        ...cards,
        dynamic_fields: updatedDynamicFields,
      });

      toast.success(`Category "${category}" deleted`);
    }
  };

  // Fetch domains
  const fetchDomains = async () => {
    setIsLoading(true);
    try {
      const data = await getDomain();
      setCareerFields(data.data);
    } catch (error) {
      console.error("Error fetching domains:", error);
      toast.error("Failed to fetch career fields");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditIndex(null);
    setEditedField("");
  };

  // Add new career field
  const handleAddCareerField = () => {
    setIsAdding(true);
  };

  // Save new career field
  const handleSaveCareerField = async () => {
    if (newField.trim() === "") {
      toast.error("Career field name cannot be empty");
      return;
    }

    try {
      const response = await createDomain(newField);
      setCareerFields((prevFields) => [
        ...prevFields,
        { domain_name: response.data.domain_name, id: response.data.id },
      ]);
      setNewField("");
      setIsAdding(false);
      fetchDomains();
      toast.success("Career field added successfully");
    } catch (error) {
      console.error("Error saving domain:", error);
      toast.error("Failed to add career field");
    }
  };

  // Edit career field
  const handleEditCareerField = (id) => {
    const field = careerFields.find((field) => field.id === id);
    setEditIndex(id);
    setEditedField(field.domain_name);
    setSelectedFieldId(id);
    setShowOptions(null);
  };

  // Save edited career field
  const handleSaveEditedField = async () => {
    if (editedField.trim() === "") {
      toast.error("Career field name cannot be empty");
      return;
    }

    const updatedField = {
      ...careerFields.find((field) => field.id === selectedFieldId),
      domain_name: editedField,
    };

    try {
      await updateDomain(selectedFieldId, { domain_name: editedField });

      const updatedCareerFields = careerFields.map((field) =>
        field.id === selectedFieldId ? updatedField : field
      );
      setCareerFields(updatedCareerFields);

      setEditIndex(null);
      setEditedField("");
      fetchDomains();
      toast.success("Career field updated successfully");
    } catch (error) {
      console.error("Error saving edited career field:", error);
      toast.error("Failed to update career field");
    }
  };

  // Delete career field
  const handleDeleteCareerField = async (id) => {
    if (
      window.confirm(
        "Are you sure you want to delete this career field? This action cannot be undone."
      )
    ) {
      try {
        await deleteDomain(id);
        setCareerFields(careerFields.filter((field) => field.id !== id));
        toast.success("Career field deleted successfully");

        // If we deleted the currently selected field, reset the selection
        if (id === selectedFieldId) {
          setSelectedFieldId("");
          setCards({
            dynamic_fields: {},
            university_name_list: [],
            link_list: {},
          });
        }
      } catch (error) {
        console.error("Error deleting career field:", error);
        toast.error("Failed to delete career field");
      }
    }
    setShowOptions(null);
  };

  // Handle career field selection
  const handleCareerFieldChange = async (event) => {
    const selectedId = event.target.value;
    setSelectedFieldId(selectedId);

    if (selectedId) {
      setIsLoading(true);
      try {
        const data = await getDomainById(selectedId);
        setCards({
          dynamic_fields: normalizeDynamicFields(
            data.data.dynamic_fields || {}
          ),
          university_name_list: data.data.university_name_list || [],
          link_list: data.data.link_list || {},
          sample_document_lists: data.data.sample_document_lists || null,
        });
      } catch (error) {
        console.error("Error fetching domain data:", error);
        toast.error("Failed to load career field details");
        setCards({
          dynamic_fields: {},
          university_name_list: [],
          link_list: {},
        });
      } finally {
        setIsLoading(false);
      }
    } else {
      setCards({
        dynamic_fields: {},
        university_name_list: [],
        link_list: {},
      });
    }
  };

  // Save all changes
  const handleSaveChanges = async () => {
    if (!selectedFieldId) {
      toast.error("Please select a career field first");
      return;
    }

    setIsLoading(true);
    try {
      const updatedData = {
        domain_name: cards.domain_name,
        university_name_list: cards.university_name_list,
        link_list: cards.link_list,
        dynamic_fields: cards.dynamic_fields,
        sample_document_lists: cards.sample_document_lists,
      };

      await updateDomain(selectedFieldId, updatedData);
      setIsEditing(false);
      fetchDomains();
      toast.success("Changes saved successfully");
    } catch (error) {
      console.error("Error updating domain:", error);
      toast.error("Failed to save changes");
    } finally {
      setIsLoading(false);
    }
  };

  // Cancel adding career field
  const handleCancelCareerField = () => {
    setNewField("");
    setIsAdding(false);
  };

  // Upload document
  const handleUploadDocument = async () => {
    if (!selectedFieldId) {
      toast.error("Please select a career field first");
      return;
    }

    if (selectedFiles.length === 0) {
      toast.error("Please select at least one file to upload.");
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();

      selectedFiles.forEach((file) => {
        formData.append("field_key", "sample_document_lists");
        formData.append("file_data", file);
      });

      await uploadDocuments(selectedFieldId, formData);

      // Refresh domain data
      const data = await getDomainById(selectedFieldId);

      setCards({
        ...cards,
        dynamic_fields: normalizeDynamicFields(data.data.dynamic_fields || {}),
        university_name_list: data.data.university_name_list || [],
        link_list: data.data.link_list || {},
        sample_document_lists: data.data.sample_document_lists || null,
      });

      // Clear selected files
      setSelectedFiles([]);
      fileInputRef.current.value = null;
      toast.success("Documents uploaded successfully");
    } catch (err) {
      console.error("Upload failed:", err);
      toast.error("Failed to upload documents");
    } finally {
      setIsUploading(false);
    }
  };

  // Delete document
  const handleDeleteDocument = async (filePath, index) => {
    if (window.confirm("Are you sure you want to delete this document?")) {
      try {
        await deleteDocument(
          selectedFieldId,
          "sample_document_lists",
          filePath
        );

        const updatedDocs = [...cards.sample_document_lists];
        updatedDocs.splice(index, 1);
        setCards((prevCards) => ({
          ...prevCards,
          sample_document_lists: updatedDocs,
        }));

        toast.success("Document deleted successfully");
      } catch (err) {
        console.error("Failed to delete document:", err);
        toast.error("Failed to delete document");
      }
    }
  };

  // Load domains on component mount
  useEffect(() => {
    fetchDomains();
  }, []);

  const isSideBarOpen = useSelector((state) => state.application.isSideBarOpen);

  return (
    <div
      className={`flex-1 bg-gray-50 ${
        isSideBarOpen ? "ml-80" : "ml-20"
      } min-h-screen`}
    >
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="p-8 max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 pb-4 border-b border-gray-200">
          <div>
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-600 hover:text-gray-800 mb-4"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-1"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                  clipRule="evenodd"
                />
              </svg>
              Back to Career Counseling
            </button>
            <h1 className="text-3xl font-bold text-gray-800">
              Manage Preloaded Details
            </h1>
            <p className="text-gray-600 mt-1">
              Configure career fields and their associated content
            </p>
          </div>

          {selectedFieldId && (
            <button
              onClick={isEditing ? handleSaveChanges : toggleEditMode}
              disabled={isLoading}
              className={`mt-4 sm:mt-0 px-6 py-3 rounded-lg flex items-center shadow-sm transition-all ${
                isEditing
                  ? "bg-green-600 hover:bg-green-700 text-white"
                  : "bg-purple-700 hover:bg-purple-800 text-white"
              } ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              {isLoading ? (
                <svg
                  className="animate-spin h-5 w-5 mr-2"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              ) : isEditing ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
              )}
              {isLoading
                ? "Processing..."
                : isEditing
                ? "Save All Changes"
                : "Edit Content"}
            </button>
          )}
        </div>

        {/* Career Fields Section */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 mb-8">
          <div className="flex items-center mb-4">
            <div className="bg-purple-100 p-2 rounded-full mr-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-purple-700"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z"
                  clipRule="evenodd"
                />
                <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-800">
              Career Fields
            </h2>
          </div>

          <div className="overflow-hidden border border-gray-200 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading && careerFields.length === 0 ? (
                  <tr>
                    <td
                      colSpan="2"
                      className="px-6 py-4 whitespace-nowrap text-center text-gray-500"
                    >
                      <div className="flex justify-center">
                        <svg
                          className="animate-spin h-5 w-5 text-purple-700"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                      </div>
                    </td>
                  </tr>
                ) : careerFields.length === 0 ? (
                  <tr>
                    <td
                      colSpan="2"
                      className="px-6 py-4 whitespace-nowrap text-center text-gray-500"
                    >
                      No career fields available
                    </td>
                  </tr>
                ) : (
                  careerFields.map((field) => (
                    <tr
                      key={field.id}
                      className={
                        field.id === selectedFieldId ? "bg-purple-50" : ""
                      }
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editIndex === field.id ? (
                          <div className="flex items-center">
                            <input
                              type="text"
                              value={editedField}
                              onChange={(e) => setEditedField(e.target.value)}
                              className="border p-2 rounded w-full"
                              placeholder="Enter field name"
                            />
                          </div>
                        ) : (
                          <div className="flex items-center">
                            <span className="text-gray-900 font-medium">
                              {field.domain_name}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {editIndex === field.id ? (
                          <div className="space-x-2">
                            <button
                              onClick={handleSaveEditedField}
                              className="text-green-600 hover:text-green-900 bg-green-100 px-3 py-1 rounded"
                            >
                              Save
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              className="text-red-600 hover:text-red-900 bg-red-100 px-3 py-1 rounded"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="space-x-2">
                            <button
                              onClick={() => {
                                setSelectedFieldId(field.id);
                                handleCareerFieldChange({
                                  target: { value: field.id },
                                });
                              }}
                              className={`text-indigo-600 hover:text-indigo-900 ${
                                field.id === selectedFieldId
                                  ? "bg-indigo-100"
                                  : ""
                              } px-3 py-1 rounded`}
                            >
                              Select
                            </button>
                            <button
                              onClick={() => handleEditCareerField(field.id)}
                              className="text-yellow-600 hover:text-yellow-900 bg-yellow-100 px-3 py-1 rounded"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteCareerField(field.id)}
                              className="text-red-600 hover:text-red-900 bg-red-100 px-3 py-1 rounded"
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {isAdding ? (
            <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <h3 className="text-lg font-medium text-gray-800 mb-2">
                Add New Career Field
              </h3>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  value={newField}
                  onChange={(e) => setNewField(e.target.value)}
                  placeholder="Enter career field name"
                  className="flex-1 border p-2 rounded"
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleSaveCareerField}
                    className="bg-green-600 text-white py-2 px-4 rounded hover:bg-green-700 transition"
                  >
                    Save
                  </button>
                  <button
                    onClick={handleCancelCareerField}
                    className="bg-gray-300 text-gray-800 py-2 px-4 rounded hover:bg-gray-400 transition"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-4">
              <button
                onClick={handleAddCareerField}
                className="bg-purple-600 text-white py-2 px-4 rounded flex items-center hover:bg-purple-700 transition"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                    clipRule="evenodd"
                  />
                </svg>
                Add New Career Field
              </button>
            </div>
          )}
        </div>

        {/* Selected Career Field Content Section */}
        {selectedFieldId && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-center mb-6">
                <div className="bg-blue-100 p-2 rounded-full mr-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-blue-700"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-800">
                    Content for{" "}
                    {careerFields.find((f) => f.id === selectedFieldId)
                      ?.domain_name || "Selected Field"}
                  </h2>
                  <p className="text-gray-500 text-sm mt-1">
                    Manage universities, links, documents, and dynamic content
                    for this career field
                  </p>
                </div>
              </div>

              {/* Tabs */}
              <div className="border-b border-gray-200 mb-6">
                <div className="flex overflow-x-auto">
                  <button
                    onClick={() => setActiveTab("universities")}
                    className={`px-4 py-2 font-medium text-sm ${
                      activeTab === "universities"
                        ? "border-b-2 border-blue-500 text-blue-600"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    Universities
                  </button>
                  <button
                    onClick={() => setActiveTab("links")}
                    className={`px-4 py-2 font-medium text-sm ${
                      activeTab === "links"
                        ? "border-b-2 border-blue-500 text-blue-600"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    Links
                  </button>
                  <button
                    onClick={() => setActiveTab("documents")}
                    className={`px-4 py-2 font-medium text-sm ${
                      activeTab === "documents"
                        ? "border-b-2 border-blue-500 text-blue-600"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    Documents
                  </button>
                  <button
                    onClick={() => setActiveTab("categories")}
                    className={`px-4 py-2 font-medium text-sm ${
                      activeTab === "categories"
                        ? "border-b-2 border-blue-500 text-blue-600"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    Categories
                  </button>
                </div>
              </div>

              {isLoading ? (
                <div className="flex justify-center items-center py-20">
                  <svg
                    className="animate-spin h-10 w-10 text-blue-500"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                </div>
              ) : (
                <>
                  {/* Universities Tab */}
                  {activeTab === "universities" && (
                    <div className="bg-white rounded-lg">
                      <div className="mb-4 flex justify-between items-center">
                        <h3 className="text-lg font-medium text-gray-800">
                          Universities
                        </h3>
                        {isEditing && (
                          <button
                            onClick={() => {
                              const updatedUniversities = [
                                ...cards.university_name_list,
                                "",
                              ];
                              setCards((prevCards) => ({
                                ...prevCards,
                                university_name_list: updatedUniversities,
                              }));
                            }}
                            className="text-blue-600 flex items-center text-sm hover:text-blue-800"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-4 w-4 mr-1"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                                clipRule="evenodd"
                              />
                            </svg>
                            Add University
                          </button>
                        )}
                      </div>
                      <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                        {cards?.university_name_list?.length === 0 ? (
                          <p className="text-gray-500 text-center py-4">
                            No universities added yet
                          </p>
                        ) : (
                          <ul className="divide-y divide-gray-200">
                            {cards?.university_name_list?.map((uni, index) => (
                              <li
                                key={index}
                                className="py-3 flex items-center justify-between"
                              >
                                {isEditing ? (
                                  <input
                                    type="text"
                                    value={uni}
                                    onChange={(e) => {
                                      const updatedUniversities = [
                                        ...cards.university_name_list,
                                      ];
                                      updatedUniversities[index] =
                                        e.target.value;
                                      setCards((prevCards) => ({
                                        ...prevCards,
                                        university_name_list:
                                          updatedUniversities,
                                      }));
                                    }}
                                    className="border border-gray-300 rounded p-2 w-full"
                                    placeholder="Enter university name"
                                  />
                                ) : (
                                  <span className="text-gray-700">{uni}</span>
                                )}
                                {isEditing && (
                                  <button
                                    onClick={() => {
                                      const updatedUniversities =
                                        cards.university_name_list.filter(
                                          (_, i) => i !== index
                                        );
                                      setCards((prevCards) => ({
                                        ...prevCards,
                                        university_name_list:
                                          updatedUniversities,
                                      }));
                                    }}
                                    className="ml-2 text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50"
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className="h-5 w-5"
                                      viewBox="0 0 20 20"
                                      fill="currentColor"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  </button>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Links Tab */}
                  {activeTab === "links" && (
                    <div className="bg-white rounded-lg">
                      <div className="mb-4 flex justify-between items-center">
                        <h3 className="text-lg font-medium text-gray-800">
                          Links
                        </h3>
                        {isEditing && (
                          <button
                            onClick={() => {
                              const updatedLinks = { ...cards.link_list };
                              const newLinkName = `New Link ${
                                Object.keys(cards.link_list).length + 1
                              }`;
                              updatedLinks[newLinkName] = "";
                              setCards((prevCards) => ({
                                ...prevCards,
                                link_list: updatedLinks,
                              }));
                            }}
                            className="text-blue-600 flex items-center text-sm hover:text-blue-800"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-4 w-4 mr-1"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                                clipRule="evenodd"
                              />
                            </svg>
                            Add Link
                          </button>
                        )}
                      </div>
                      <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                        {Object.keys(cards?.link_list || {}).length === 0 ? (
                          <p className="text-gray-500 text-center py-4">
                            No links added yet
                          </p>
                        ) : (
                          <ul className="divide-y divide-gray-200">
                            {Object.entries(cards?.link_list || {}).map(
                              ([name, urlObj], index) => {
                                const url =
                                  typeof urlObj === "object" && urlObj.url
                                    ? urlObj.url
                                    : urlObj;
                                const linkId = `link-${index}`;

                                return (
                                  <li key={linkId} className="py-3">
                                    {isEditing ? (
                                      <div className="flex flex-col sm:flex-row gap-2">
                                        <div className="w-full sm:w-1/3">
                                          <label className="block text-xs text-gray-500 mb-1">
                                            Link Name
                                          </label>
                                          <input
                                            type="text"
                                            value={name}
                                            onChange={(e) => {
                                              const updatedLinks = {};
                                              Object.entries(
                                                cards.link_list
                                              ).forEach(([key, value]) => {
                                                if (key !== name) {
                                                  updatedLinks[key] = value;
                                                } else {
                                                  updatedLinks[e.target.value] =
                                                    url;
                                                }
                                              });
                                              setCards({
                                                ...cards,
                                                link_list: updatedLinks,
                                              });
                                            }}
                                            className="border border-gray-300 rounded p-2 w-full"
                                            placeholder="Enter link name"
                                          />
                                        </div>
                                        <div className="w-full sm:w-2/3 flex">
                                          <div className="flex-grow">
                                            <label className="block text-xs text-gray-500 mb-1">
                                              URL
                                            </label>
                                            <input
                                              type="text"
                                              value={url}
                                              onChange={(e) => {
                                                const updatedLinks = {
                                                  ...cards.link_list,
                                                };
                                                updatedLinks[name] =
                                                  e.target.value;
                                                setCards({
                                                  ...cards,
                                                  link_list: updatedLinks,
                                                });
                                              }}
                                              className="border border-gray-300 rounded p-2 w-full"
                                              placeholder="Enter URL"
                                            />
                                          </div>
                                          <div className="flex items-end ml-2">
                                            <button
                                              onClick={() => {
                                                const updatedLinks = {
                                                  ...cards.link_list,
                                                };
                                                delete updatedLinks[name];
                                                setCards({
                                                  ...cards,
                                                  link_list: updatedLinks,
                                                });
                                              }}
                                              className="text-red-500 hover:text-red-700 p-2 rounded-full hover:bg-red-50"
                                            >
                                              <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="h-5 w-5"
                                                viewBox="0 0 20 20"
                                                fill="currentColor"
                                              >
                                                <path
                                                  fillRule="evenodd"
                                                  d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                                  clipRule="evenodd"
                                                />
                                              </svg>
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="flex flex-col">
                                        <span className="font-medium text-gray-900">
                                          {name}
                                        </span>
                                        <a
                                          href={url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-blue-600 hover:text-blue-800 break-all"
                                        >
                                          {url}
                                        </a>
                                      </div>
                                    )}
                                  </li>
                                );
                              }
                            )}
                          </ul>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Documents Tab */}
                  {activeTab === "documents" && (
                    <div className="bg-white rounded-lg">
                      <div className="mb-4 flex justify-between items-center">
                        <h3 className="text-lg font-medium text-gray-800">
                          Documents
                        </h3>
                      </div>
                      <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                        {isEditing && (
                          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <h4 className="text-md font-medium text-blue-800 mb-2">
                              Upload Documents
                            </h4>
                            <div className="flex flex-col sm:flex-row gap-3">
                              <div className="flex-grow">
                                <input
                                  ref={fileInputRef}
                                  type="file"
                                  multiple
                                  onChange={(e) => {
                                    const files = Array.from(e.target.files);
                                    setSelectedFiles((prev) => [
                                      ...prev,
                                      ...files,
                                    ]);
                                  }}
                                  className="w-full block text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-white p-2"
                                />
                                {selectedFiles.length > 0 && (
                                  <div className="mt-2 text-sm text-gray-600">
                                    {selectedFiles.length} file(s) selected
                                  </div>
                                )}
                              </div>
                              <button
                                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center justify-center"
                                onClick={handleUploadDocument}
                                disabled={
                                  isUploading || selectedFiles.length === 0
                                }
                              >
                                {isUploading ? (
                                  <>
                                    <svg
                                      className="animate-spin h-5 w-5 mr-2"
                                      xmlns="http://www.w3.org/2000/svg"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                    >
                                      <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                      ></circle>
                                      <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                      ></path>
                                    </svg>
                                    Uploading...
                                  </>
                                ) : (
                                  <>
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className="h-5 w-5 mr-2"
                                      viewBox="0 0 20 20"
                                      fill="currentColor"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                    Upload
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        )}

                        {(cards.sample_document_lists || []).length === 0 ? (
                          <p className="text-gray-500 text-center py-8">
                            No documents uploaded yet
                          </p>
                        ) : (
                          <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {(cards.sample_document_lists || []).map(
                              (file, index) => {
                                const filePath =
                                  typeof file === "string"
                                    ? file
                                    : file.path || file.name || "";

                                const displayName =
                                  typeof file === "string"
                                    ? file.split(
                                        "post_services_sample_documents/"
                                      )[1] || `Document ${index + 1}`
                                    : file.name || `Document ${index + 1}`;

                                const fileExt = displayName
                                  .split(".")
                                  .pop()
                                  .toLowerCase();

                                // Determine icon based on file extension
                                let fileIcon;
                                if (["pdf"].includes(fileExt)) {
                                  fileIcon = (
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className="h-8 w-8 text-red-500"
                                      viewBox="0 0 20 20"
                                      fill="currentColor"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  );
                                } else if (["doc", "docx"].includes(fileExt)) {
                                  fileIcon = (
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className="h-8 w-8 text-blue-500"
                                      viewBox="0 0 20 20"
                                      fill="currentColor"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  );
                                } else if (["xls", "xlsx"].includes(fileExt)) {
                                  fileIcon = (
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className="h-8 w-8 text-green-500"
                                      viewBox="0 0 20 20"
                                      fill="currentColor"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  );
                                } else if (
                                  ["jpg", "jpeg", "png", "gif"].includes(
                                    fileExt
                                  )
                                ) {
                                  fileIcon = (
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className="h-8 w-8 text-purple-500"
                                      viewBox="0 0 20 20"
                                      fill="currentColor"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  );
                                } else {
                                  fileIcon = (
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className="h-8 w-8 text-gray-500"
                                      viewBox="0 0 20 20"
                                      fill="currentColor"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  );
                                }

                                return (
                                  <li
                                    key={index}
                                    className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                                  >
                                    <div className="mr-3 flex-shrink-0">
                                      {fileIcon}
                                    </div>
                                    <div className="flex-grow min-w-0">
                                      <p
                                        className="text-sm font-medium text-gray-900 truncate"
                                        title={displayName}
                                      >
                                        {displayName}
                                      </p>
                                      <p className="text-xs text-gray-500">
                                        {fileExt.toUpperCase()} Document
                                      </p>
                                    </div>
                                    {isEditing && (
                                      <button
                                        className="ml-2 text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50"
                                        onClick={() =>
                                          handleDeleteDocument(filePath, index)
                                        }
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          className="h-5 w-5"
                                          viewBox="0 0 20 20"
                                          fill="currentColor"
                                        >
                                          <path
                                            fillRule="evenodd"
                                            d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                            clipRule="evenodd"
                                          />
                                        </svg>
                                      </button>
                                    )}
                                  </li>
                                );
                              }
                            )}
                          </ul>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Categories Tab */}
                  {activeTab === "categories" && (
                    <div className="bg-white rounded-lg">
                      <div className="mb-4 flex justify-between items-center">
                        <h3 className="text-lg font-medium text-gray-800">
                          Categories
                        </h3>
                        {isEditing && (
                          <button
                            onClick={addCard}
                            className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition flex items-center"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-4 w-4 mr-1"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                                clipRule="evenodd"
                              />
                            </svg>
                            Add Category
                          </button>
                        )}
                      </div>

                      {/* Dynamic Categories */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {categoryOrder.length === 0 ? (
                          <div className="col-span-full bg-gray-50 p-8 rounded-lg border border-gray-200 text-center">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-12 w-12 mx-auto text-gray-400 mb-4"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M5 4a3 3 0 00-3 3v6a3 3 0 003 3h10a3 3 0 003-3V7a3 3 0 00-3-3H5zm0 2a1 1 0 00-1 1v6a1 1 0 001 1h10a1 1 0 001-1V7a1 1 0 00-1-1H5z"
                                clipRule="evenodd"
                              />
                            </svg>
                            <p className="text-gray-600 mb-4">
                              No categories have been created yet
                            </p>
                            {isEditing && (
                              <button
                                onClick={addCard}
                                className="bg-blue-600 text-white py-2 px-6 rounded-lg hover:bg-blue-700 transition inline-flex items-center"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-5 w-5 mr-2"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                                Create First Category
                              </button>
                            )}
                          </div>
                        ) : (
                          cards?.dynamic_fields &&
                          typeof cards?.dynamic_fields === "object" &&
                          categoryOrder.map((category, orderIndex) => {
                            const items = cards.dynamic_fields[category];
                            if (!items) return null;

                            return (
                              <div
                                key={`category-${category}`}
                                className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
                              >
                                {/* Editable Category Title */}
                                {editingCategory === category ? (
                                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-4">
                                    <input
                                      type="text"
                                      value={editedCategoryName}
                                      onChange={(e) =>
                                        setEditedCategoryName(e.target.value)
                                      }
                                      className="flex-grow text-lg font-semibold text-gray-800 border border-blue-300 rounded p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                      placeholder="Category name"
                                    />
                                    <div className="flex gap-2">
                                      <button
                                        onClick={saveEditedCategory}
                                        className="px-3 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition flex items-center"
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          className="h-4 w-4 mr-1"
                                          viewBox="0 0 20 20"
                                          fill="currentColor"
                                        >
                                          <path
                                            fillRule="evenodd"
                                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                            clipRule="evenodd"
                                          />
                                        </svg>
                                        Save
                                      </button>
                                      <button
                                        onClick={cancelEditingCategory}
                                        className="px-3 py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition flex items-center"
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          className="h-4 w-4 mr-1"
                                          viewBox="0 0 20 20"
                                          fill="currentColor"
                                        >
                                          <path
                                            fillRule="evenodd"
                                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                            clipRule="evenodd"
                                          />
                                        </svg>
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex justify-between items-center mb-4 pb-2 border-b border-gray-100">
                                    <h3 className="text-lg font-semibold text-gray-800">
                                      {category}
                                    </h3>
                                    {isEditing && (
                                      <div className="flex gap-1">
                                        <button
                                          onClick={() =>
                                            startEditingCategory(category)
                                          }
                                          className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50"
                                          title="Edit category name"
                                        >
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-4 w-4"
                                            viewBox="0 0 20 20"
                                            fill="currentColor"
                                          >
                                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                          </svg>
                                        </button>
                                        <button
                                          onClick={() =>
                                            handleDeleteCategory(category)
                                          }
                                          className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50"
                                          title="Delete category"
                                        >
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-4 w-4"
                                            viewBox="0 0 20 20"
                                            fill="currentColor"
                                          >
                                            <path
                                              fillRule="evenodd"
                                              d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                              clipRule="evenodd"
                                            />
                                          </svg>
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Category Items */}
                                <div className="space-y-1">
                                  {items && items.length > 0 ? (
                                    <ul className="space-y-2">
                                      {items.map((item, itemIndex) => {
                                        // Extract the item value regardless of format
                                        const itemValue =
                                          typeof item === "object" &&
                                          item !== null
                                            ? item.value
                                            : item;
                                        const itemId =
                                          typeof item === "object" && item.id
                                            ? item.id
                                            : `${category}-item-${itemIndex}`;

                                        return (
                                          <li
                                            key={`item-${itemId}`}
                                            className={`rounded-lg ${
                                              isEditing ? "bg-gray-50" : ""
                                            }`}
                                          >
                                            {isEditing ? (
                                              <div className="flex items-center p-2">
                                                <input
                                                  type="text"
                                                  value={itemValue}
                                                  onChange={(e) => {
                                                    handleItemUpdate(
                                                      category,
                                                      itemIndex,
                                                      e.target.value
                                                    );
                                                  }}
                                                  className="border border-gray-300 rounded p-2 w-full focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                  placeholder="Enter item value"
                                                />
                                                <button
                                                  onClick={() =>
                                                    handleDeleteItem(
                                                      category,
                                                      itemIndex
                                                    )
                                                  }
                                                  className="ml-2 text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50"
                                                >
                                                  <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="h-5 w-5"
                                                    viewBox="0 0 20 20"
                                                    fill="currentColor"
                                                  >
                                                    <path
                                                      fillRule="evenodd"
                                                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                                      clipRule="evenodd"
                                                    />
                                                  </svg>
                                                </button>
                                              </div>
                                            ) : (
                                              <div className="p-2">
                                                <div className="text-gray-700">
                                                  {parseTextWithLinks(
                                                    itemValue
                                                  )}
                                                </div>
                                              </div>
                                            )}
                                          </li>
                                        );
                                      })}
                                    </ul>
                                  ) : (
                                    <p className="text-gray-500 text-center py-4">
                                      No items in this category
                                    </p>
                                  )}

                                  {/* Add Item Button */}
                                  {isEditing && (
                                    <div className="mt-3 text-center">
                                      <button
                                        onClick={() => handleAddItem(category)}
                                        className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition inline-flex items-center"
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          className="h-4 w-4 mr-1"
                                          viewBox="0 0 20 20"
                                          fill="currentColor"
                                        >
                                          <path
                                            fillRule="evenodd"
                                            d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                                            clipRule="evenodd"
                                          />
                                        </svg>
                                        Add Item
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ManagePreloadedDetails;
